import { sendUpdate } from '@/api/requests';
import {
  FIRST_DOWN_YARDAGE,
  KICKOFF_YARDLINE,
  NON_KICKOFF_TOUCHBACK_YARDLINE,
  PAT_YARDLINE,
  SAFTEY_KICKOFF_YARDLINE,
  TEventTypes,
  TPlayDesigns,
  TPlayerRole,
  TPlayResultTypes,
  TQuarters,
} from '@/constants';
import {
  IEvent,
  IGameMessage,
  IGameTeams,
  IMachineContextCurrentPlay,
  IMessageBody,
  IPlayerOption,
  IPlayerStat,
  IPlayflowPlay,
  IRosterPlayer,
  ITeamPlayers,
  TOffDef,
  TTeam,
  TTeamSide,
} from '@/types';
import { useUserStore } from '@/stores/user';
import type { usePlayFlowState } from '../playFlow';
import type { useGeneralState } from '../general';
import { find, isNumber, last, omit, orderBy } from 'lodash';
import {
  containsInterceptionResult,
  containsOffensiveTouchdownResult,
  isCompletePass,
  isMultiTovPlay,
  touchdownResults,
  turnoverResults,
} from '@/utilities/playResultTypes';

export const getDistanceTraveled = (
  isHomeDriveDirection: boolean,
  startingYardLine: number,
  isHomeStartingSide: boolean,
  isHomePostPlaySide: boolean,
  postPlayYardLine: number
): number => {
  const didCrossMidfield = isHomeStartingSide !== isHomePostPlaySide || postPlayYardLine === 50;
  const isNegativePlayEndingOn50 = () => {
    if (postPlayYardLine !== 50) {
      return false;
    }
    return (isHomeStartingSide && isHomeDriveDirection) || (!isHomeStartingSide && !isHomeDriveDirection);
  };

  // Determine distance traveled
  if (
    ((isHomeStartingSide && !isHomeDriveDirection) || (!isHomeStartingSide && isHomeDriveDirection)) &&
    !didCrossMidfield
  ) {
    return postPlayYardLine - startingYardLine;
  }
  if (
    ((isHomeStartingSide && isHomeDriveDirection) || (!isHomeStartingSide && !isHomeDriveDirection)) &&
    !didCrossMidfield
  ) {
    return startingYardLine - postPlayYardLine;
  }
  if (
    (didCrossMidfield && isHomeDriveDirection !== isHomePostPlaySide && postPlayYardLine !== 50) ||
    isNegativePlayEndingOn50()
  ) {
    return (50 - postPlayYardLine + 50 - startingYardLine) * -1;
  }

  return 50 - postPlayYardLine + 50 - startingYardLine;
};

export const adjustPlayerStats = (play: IPlayflowPlay, banjoPlay: IMachineContextCurrentPlay) => {
  if (!play.playerStats || banjoPlay.options.isIntenionalGrounding) {
    return;
  }

  const isPenalty: boolean = [
    TPlayResultTypes.PENALTY_HOME,
    TPlayResultTypes.PENALTY_AWAY,
    TPlayResultTypes.PENALTY_BOTH,
  ].includes(play.playResultTypeId);
  const isInterception: boolean =
    play.playDesignId === TPlayDesigns.PASS && containsInterceptionResult(banjoPlay.resultType);

  // remove player stats if no play or offensive penalty or penalty both
  if (play.playDesignId === TPlayDesigns.NO_PLAY || isPenalty) {
    play.playerStats = [];
  }

  // if interception ensure that passer att is counted with 0 yardage
  if (isInterception && play.playerStats.length) {
    play.playerStats = play.playerStats.filter((stat) => stat.roleId === TPlayerRole.PASSER);
    play.playerStats[0].statisticalYardage = 0;
    return;
  }

  if (!play.fumbleYardLine && !play.penaltyYardLine) {
    return;
  }

  // adjust statistical yardage based on point of fumble/ pre-penalty yardage selection
  const statisticalYardage = getDistanceTraveled(
    !play.homeTeamStartedWithPossession,
    play.startingYardLine,
    play.startingIsHomeTeamSideOfField,
    play.fumbleIsHomeTeamSideOfField ?? play.penaltyIsHomeTeamSideOfField,
    play.fumbleYardLine ?? play.penaltyYardLine
  );

  play.playerStats.forEach((stat) => {
    stat.statisticalYardage = statisticalYardage;
  });
};

// calculate distance to goal line
export function getDistanceToGoalLine(yardline: number, side: TTeamSide, driveDirection: TTeamSide) {
  const isBallOnLeftSide = side === TTeamSide.LEFT;
  const isDriveDirectionLeft = driveDirection === TTeamSide.LEFT;
  const isBallOnRightSide = side === TTeamSide.RIGHT;
  const isDriveDirectionRight = driveDirection === TTeamSide.RIGHT;

  if (isBallOnLeftSide && isDriveDirectionLeft) {
    return yardline;
  }

  if (isBallOnRightSide && isDriveDirectionRight) {
    return yardline;
  }

  if (isBallOnLeftSide && isDriveDirectionRight) {
    return 100 - yardline;
  }

  if (isBallOnRightSide && isDriveDirectionLeft) {
    return 100 - yardline;
  }
  return 0;
}

export const getFirstDownYardage = (
  currentYardline: number,
  currentSide: TTeamSide,
  driveDirection: TTeamSide,
  isKickOffOrPunt = false,
  isTov = false
): number => {
  // need to flip direction for kickoff/punt returns and TOV
  let dir = driveDirection;
  if (isKickOffOrPunt || isTov) {
    dir = driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT;
  }

  // Close to goaline
  if (FIRST_DOWN_YARDAGE >= currentYardline && currentSide === dir) {
    return currentYardline;
  }

  // ensure distance is never greater than yards to goaline
  const yardsToGoaline = getDistanceToGoalLine(currentYardline, currentSide, dir);
  return FIRST_DOWN_YARDAGE <= yardsToGoaline ? FIRST_DOWN_YARDAGE : yardsToGoaline;
};

export const getResultingDownAndDistance = (
  oldDown: number | null,
  oldDistance: number | null,
  driveDirection: TTeamSide,
  oldYardLine: number,
  oldSide: TTeamSide,
  currentPlay: IMachineContextCurrentPlay
) => {
  const isTurnover = !!currentPlay.options?.isTov || !!currentPlay.options?.isTouchbackTov;
  const isNoPlay = currentPlay.playDesignId === TPlayDesigns.NO_PLAY;
  const didCrossMidfield = oldSide !== currentPlay.postPlaySide || currentPlay.postPlayYardLine === 50;
  const isNegativePlayEndingOn50 = () => {
    if (currentPlay.postPlayYardLine !== 50) {
      return false;
    }
    return (
      (oldSide === TTeamSide.LEFT && driveDirection === TTeamSide.LEFT) ||
      (oldSide === TTeamSide.RIGHT && driveDirection === TTeamSide.RIGHT)
    );
  };

  let distanceTraveled: number;
  let down = oldDown;
  let distance = oldDistance;
  let isTurnoverOnDowns = false;
  let isDefiniteFirstDown = false;

  // Determine distance traveled
  if (
    ((oldSide === TTeamSide.LEFT && driveDirection === TTeamSide.RIGHT) ||
      (oldSide === TTeamSide.RIGHT && driveDirection === TTeamSide.LEFT)) &&
    !didCrossMidfield
  ) {
    distanceTraveled = currentPlay.postPlayYardLine - oldYardLine;
  } else if (
    ((oldSide === TTeamSide.LEFT && driveDirection === TTeamSide.LEFT) ||
      (oldSide === TTeamSide.RIGHT && driveDirection === TTeamSide.RIGHT)) &&
    !didCrossMidfield
  ) {
    distanceTraveled = oldYardLine - currentPlay.postPlayYardLine;
  } else if (
    (didCrossMidfield &&
      driveDirection !== currentPlay.postPlaySide &&
      currentPlay.postPlayYardLine !== 50) ||
    isNegativePlayEndingOn50()
  ) {
    distanceTraveled = (50 - currentPlay.postPlayYardLine + 50 - oldYardLine) * -1;
  } else {
    distanceTraveled = 50 - currentPlay.postPlayYardLine + 50 - oldYardLine;
  }

  let newYardLine = currentPlay.postPlayYardLine;
  if (currentPlay.options?.isTouchbackTov) {
    newYardLine = NON_KICKOFF_TOUCHBACK_YARDLINE;
  }

  // definite first down means +2 past first down line
  if (
    !currentPlay.options?.isPreSnapPATPenalty &&
    ((distanceTraveled >= oldDistance + 2 && currentPlay.isFirstDown !== false) ||
      currentPlay.isFirstDown ||
      last(currentPlay.resultType) === TPlayResultTypes.TOUCHBACK)
  ) {
    isDefiniteFirstDown = true;
  }

  // Determine next down
  if (
    isTurnover ||
    currentPlay.isFirstDown ||
    (distanceTraveled >= oldDistance && currentPlay.isFirstDown !== false) ||
    last(currentPlay.resultType) === TPlayResultTypes.TOUCHBACK ||
    (!oldDown && !oldDistance) ||
    isMultiTovPlay(currentPlay.resultType)
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      isTurnover
    );
  } else if (currentPlay.isFirstDown === false && down < 4) {
    down += 1;
    distance -= distanceTraveled;
  } else if (
    (distanceTraveled < oldDistance && down === 4) ||
    (currentPlay.isFirstDown === false && down === 4)
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      true
    );
    isTurnoverOnDowns = true;
  } else {
    down += 1;
    distance -= distanceTraveled;
  }

  // Handle missed field goal
  if (
    currentPlay.playDesignId === TPlayDesigns.FG &&
    last(currentPlay.resultType) === TPlayResultTypes.FG_MISS
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      true
    );
  }

  // Adjust for penalty handling/no play situations
  if (
    currentPlay.options?.isPenaltyDuringReturn &&
    !!currentPlay.penalty &&
    !currentPlay.options?.isIntenionalGrounding &&
    currentPlay.penalty === TOffDef.DEFENSE &&
    currentPlay.options.isReplayDown
  ) {
    down = oldDown;
    distance = oldDistance - distanceTraveled;
    isTurnoverOnDowns = false;
  } else if (
    currentPlay.options?.isPenaltyDuringReturn &&
    !!currentPlay.penalty &&
    !currentPlay.options?.isIntenionalGrounding &&
    currentPlay.penalty !== TOffDef.BOTH
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      isTurnover
    );
    isTurnoverOnDowns = false;
  } else if (
    currentPlay.options?.isPenaltyOnTurnover &&
    !!currentPlay.penalty &&
    !currentPlay.options?.isIntenionalGrounding &&
    !currentPlay.options?.isPossessionRetained
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      isTurnover
    );
    isTurnoverOnDowns = false;
  } else if (
    (currentPlay.penalty === TOffDef.OFFENSE || currentPlay.penalty === TOffDef.BOTH) &&
    !currentPlay.options?.isIntenionalGrounding
  ) {
    down = oldDown;
    isTurnoverOnDowns = false;
    distance = oldDistance - distanceTraveled;
  } else if (
    currentPlay.isFirstDown !== false &&
    currentPlay.penalty === TOffDef.DEFENSE &&
    (!isNoPlay || distanceTraveled >= oldDistance) &&
    !currentPlay.options?.isIntenionalGrounding
  ) {
    down = 1;
    distance = getFirstDownYardage(
      newYardLine,
      currentPlay.postPlaySide,
      driveDirection,
      !!currentPlay.options?.isKickoffOrPunt,
      isTurnover
    );
    isTurnoverOnDowns = false;
  } else if (currentPlay.isFirstDown !== false && isNoPlay && !currentPlay.options?.isIntenionalGrounding) {
    down = oldDown;
    isTurnoverOnDowns = false;
    distance = oldDistance - distanceTraveled;
  }

  return {
    down,
    distance: Math.max(distance, 0),
    distanceTraveled,
    isTurnoverOnDowns,
    isDefiniteFirstDown,
  };
};

export const getResultingYardline = (play: IMachineContextCurrentPlay): number => {
  if (play.options?.isTouchbackTov) {
    return NON_KICKOFF_TOUCHBACK_YARDLINE;
  }
  if (play.options?.isSaftey) {
    return SAFTEY_KICKOFF_YARDLINE;
  }

  switch (last(play.resultType)) {
    case TPlayResultTypes.TD:
    case TPlayResultTypes.PASS_COMP_TD:
    case TPlayResultTypes.PASS_COMP_FUMBLE_LOST_TD:
    case TPlayResultTypes.SACK_FUMBLE_LOST_TD:
    case TPlayResultTypes.INTERCEPTION_TD:
    case TPlayResultTypes.FUMBLE_LOST_TD:
    case TPlayResultTypes.RUSH_ATTEMPT_TD:
    case TPlayResultTypes.RUSH_ATTEMPT_FUMBLE_LOST_TD:
    case TPlayResultTypes.FG_MISS_TD:
      return PAT_YARDLINE;
    case TPlayResultTypes.CONVERSION_2PT:
    case TPlayResultTypes.EXTRA_PT:
    case TPlayResultTypes.PAT_MISS:
    case TPlayResultTypes.FG:
      return KICKOFF_YARDLINE;
    case TPlayResultTypes.SAFTEY:
      return SAFTEY_KICKOFF_YARDLINE;
    case TPlayResultTypes.FG_MISS:
      return play.postPlayYardLine < NON_KICKOFF_TOUCHBACK_YARDLINE && !play.options?.isFGReturned
        ? NON_KICKOFF_TOUCHBACK_YARDLINE
        : play.postPlayYardLine;
    default:
      return play.postPlayYardLine;
  }
};

export const isReKickoffPenalty = (currentPlay: IMachineContextCurrentPlay): boolean =>
  !!currentPlay.penalty &&
  currentPlay.options?.isReplayDown &&
  currentPlay.playDesignId === TPlayDesigns.KICKOFF;

export const isLastPlayKickoffPenalty = (lastPlay: IPlayflowPlay, thisPlay: IPlayflowPlay) =>
  !!lastPlay &&
  lastPlay.playDesignId === TPlayDesigns.KICKOFF &&
  [TPlayResultTypes.PENALTY_HOME, TPlayResultTypes.PENALTY_AWAY, TPlayResultTypes.PENALTY_BOTH].includes(
    lastPlay.playResultTypeId
  ) &&
  lastPlay.homeTeamStartedWithPossession === thisPlay.homeTeamStartedWithPossession;

export const isPreSnapKickoffPenalty = (currentPlay: IMachineContextCurrentPlay): boolean =>
  !!currentPlay.penalty &&
  !currentPlay.options?.isKickoffSnapped &&
  currentPlay.playDesignId === TPlayDesigns.KICKOFF;

export const isPreSnapPenalty = (currentPlay: IMachineContextCurrentPlay): boolean => {
  if (
    (!!currentPlay.penalty &&
      (!currentPlay.playDesignId || currentPlay.playDesignId === TPlayDesigns.NO_PLAY)) ||
    isPreSnapKickoffPenalty(currentPlay)
  ) {
    return true;
  }
  return false;
};

export const getIsHomeResultingPossession = (
  currentPlay: IMachineContextCurrentPlay,
  activeTeam: TTeam,
  isTurnoverOnDowns: boolean
): boolean => {
  if (currentPlay.options?.isPossessionRetained) {
    return activeTeam === TTeam.HOME;
  }

  if (currentPlay.penalty === TOffDef.BOTH && currentPlay.playDesignId === TPlayDesigns.PUNT) {
    return activeTeam === TTeam.HOME;
  }

  if (
    (currentPlay.options?.isPenaltyDuringReturn || !!currentPlay.options?.isPenaltyAfterTurnover) &&
    !!currentPlay.penalty
  ) {
    return activeTeam !== TTeam.HOME;
  }

  // pre snap penalty will always stay on same side
  if (isPreSnapPenalty(currentPlay)) {
    return activeTeam === TTeam.HOME;
  }

  if (currentPlay.options?.isKickReturnFumbleLost) {
    // fumbles lost during return will stay with kicking team
    return activeTeam === TTeam.HOME;
  }

  if (currentPlay.options?.isKickReturnFumbleRecov) {
    // fumble recovered by return team switch poss
    return activeTeam === TTeam.AWAY;
  }

  if (currentPlay.options?.isOnsideRecover) {
    // onside recovered by kicking team no poss switch
    return activeTeam === TTeam.HOME;
  }

  const shouldSwitchPoss =
    (!!currentPlay.options?.isKickoffOrPunt &&
      !(currentPlay.options?.isPrePuntSnapPotentialTov && !currentPlay.options?.isTov)) ||
    (currentPlay.options?.isPenaltyOnTurnover && !currentPlay.options?.isPossessionRetained) ||
    !!currentPlay.options?.isTov ||
    !!currentPlay.options?.isTouchbackTov ||
    isTurnoverOnDowns;

  if (currentPlay.penalty !== TOffDef.DEFENSE) {
    return (
      (!shouldSwitchPoss && activeTeam === TTeam.HOME) || (shouldSwitchPoss && activeTeam === TTeam.AWAY)
    );
  }
  if (currentPlay.options?.isPenaltyOnTurnover && !currentPlay.options?.isPossessionRetained) {
    // Penalty on Defense during turnover, but turnover stands
    return activeTeam === TTeam.AWAY;
  }
  return activeTeam === TTeam.HOME;
};

/**
 * Returns whether resulting side is home side of field
 */
export const getResultingSideOfField = (
  teams: { left: TTeam; right: TTeam },
  playDesignId: TPlayDesigns,
  side: TTeamSide,
  isDefensivePenaltyOnPlay: boolean
): boolean => {
  if (
    (playDesignId !== TPlayDesigns.EXTRA_PT && playDesignId !== TPlayDesigns.TWO_PT_CONVERSION) ||
    ((playDesignId === TPlayDesigns.EXTRA_PT || playDesignId === TPlayDesigns.TWO_PT_CONVERSION) &&
      isDefensivePenaltyOnPlay)
  ) {
    return teams[side] === TTeam.HOME;
  }
  return !(teams[side] === TTeam.HOME);
};

export const sendPlayFlowEvent = (gameId: number, event: IEvent) => {
  const { getUserRole } = useUserStore();
  const header = {
    messageType: 1,
    scout: getUserRole(),
    clientTimestamp: new Date().toISOString(),
  };
  const body: IMessageBody = {
    gameId,
    events: [{ ...event, value: `${event.value}` }],
  };
  const gameMessage: IGameMessage = {
    header,
    body,
  };
  sendUpdate(gameMessage);
};

export const sendRosterMessage = (gameId: number, players: ITeamPlayers, teams: IGameTeams) => {
  const { getUserRole } = useUserStore();
  const formatPlayerObj = (player: IPlayerOption): IRosterPlayer => ({
    ...omit(player, ['name', 'number']),
    jersey: player.number.toString(),
  });

  const body: IMessageBody = {
    gameId,
    teams,
    roster: {
      homePlayers: {
        passer: [formatPlayerObj(players.home.Passer[0])],
        rushers: players.home.Rusher.map((player) => formatPlayerObj(player)),
        receivers: players.home.Receiver.map((player) => formatPlayerObj(player)),
      },
      awayPlayers: {
        passer: [formatPlayerObj(players.away.Passer[0])],
        rushers: players.away.Rusher.map((player) => formatPlayerObj(player)),
        receivers: players.away.Receiver.map((player) => formatPlayerObj(player)),
      },
    },
  };

  const header = {
    messageType: 1,
    scout: getUserRole(),
    clientTimestamp: new Date().toISOString(),
  };
  const gameMessage: IGameMessage = {
    header,
    body,
  };
  sendUpdate(gameMessage);
};

/**
 * Determines the next drive and drive play number.
 *
 * allPlays parameter is only required if isForInsert = true
 */
export const getDriveNumber = (
  play: Partial<IPlayflowPlay>,
  lastDriveNumber: number,
  lastDrivePlayNumber: number,
  isForInsert = false,
  allPlays: Partial<IPlayflowPlay>[] = [],
  forceNewDrive = false
): {
  nextDriveNumber: number;
  nextDrivePlayNumber: number;
} => {
  const isTouchdownResult = touchdownResults.includes(play.playResultTypeId as TPlayResultTypes);
  let numbering = {
    nextDriveNumber: lastDriveNumber,
    nextDrivePlayNumber: lastDrivePlayNumber + 1,
  };
  let isAdvanceDrive = false;

  // check if end of drive
  if (
    forceNewDrive ||
    play.playResultTypeId === TPlayResultTypes.TOV ||
    play.playResultTypeId === TPlayResultTypes.EXTRA_PT ||
    play.playResultTypeId === TPlayResultTypes.CONVERSION_2PT ||
    play.playResultTypeId === TPlayResultTypes.FG ||
    play.playResultTypeId === TPlayResultTypes.FG_MISS ||
    play.playResultTypeId === TPlayResultTypes.PAT_MISS ||
    play.playResultTypeId === TPlayResultTypes.INTERCEPTION ||
    play.playResultTypeId === TPlayResultTypes.SAFTEY ||
    (play.playDesignId === TPlayDesigns.PUNT && !isTouchdownResult) ||
    (play.playResultTypeId === TPlayResultTypes.FUMBLE_LOST &&
      play.homeTeamStartedWithPossession !== play.homeTeamEndedWithPossession) ||
    (play.homeTeamStartedWithPossession !== play.homeTeamEndedWithPossession &&
      play.playDesignId !== TPlayDesigns.KICKOFF &&
      !isTouchdownResult)
  ) {
    numbering = {
      nextDriveNumber: lastDriveNumber + 1,
      nextDrivePlayNumber: 0,
    };
    isAdvanceDrive = true;
  }

  if (!isForInsert) {
    return numbering;
  }

  if (!isAdvanceDrive) {
    const conflictingPlay = find(
      allPlays,
      (p) =>
        p.driveNumber === numbering.nextDriveNumber &&
        (p.drivePlayNumber === numbering.nextDrivePlayNumber ||
          p.drivePlayNumber === Math.floor(numbering.nextDrivePlayNumber))
    );

    return {
      nextDriveNumber: numbering.nextDriveNumber,
      nextDrivePlayNumber: conflictingPlay
        ? parseFloat((lastDrivePlayNumber + 0.01).toFixed(2))
        : numbering.nextDrivePlayNumber,
    };
  }

  const conflictingPlay = find(
    allPlays,
    (p) => p.driveNumber <= numbering.nextDriveNumber && allPlays.indexOf(p) > allPlays.indexOf(play)
  );

  return {
    nextDriveNumber: conflictingPlay
      ? parseFloat((lastDriveNumber + 0.01).toFixed(2))
      : Math.floor(numbering.nextDriveNumber),
    nextDrivePlayNumber: 0,
  };
};

export function sendPreliminaryYardsToGoalLine(
  playFlowState: ReturnType<typeof usePlayFlowState>,
  generalState: ReturnType<typeof useGeneralState>,
  yardLine: number,
  side: TTeamSide,
  currentPlay: IMachineContextCurrentPlay
) {
  const { driveDirection } = playFlowState;
  let dir = driveDirection;
  if (
    [TPlayDesigns.PUNT, TPlayDesigns.KICKOFF].includes(currentPlay.playDesignId) ||
    !!currentPlay.options?.isTov
  ) {
    dir = driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT;
  }
  const distanceToGoalLine = getDistanceToGoalLine(yardLine, side, dir);
  playFlowState.pushEvent(
    {
      eventTypeId: TEventTypes.PRELIMINARY_YARDS_TO_GOAL_LINE,
      value: distanceToGoalLine,
    },
    generalState.currentGame.gameId
  );
}

export function getResultWithMultiTovCheck(lastType: number, currentPlay: IMachineContextCurrentPlay) {
  const tovs = [TPlayResultTypes.INTERCEPTION, TPlayResultTypes.FUMBLE_LOST];
  if (tovs.includes(lastType) && !(currentPlay.options?.isTov || currentPlay.options?.isTouchbackTov)) {
    return TPlayResultTypes.FUMBLE;
  }
  return lastType;
}

export function getPlayResultId(
  currentPlay: IMachineContextCurrentPlay,
  offenseTeam: TTeam,
  isTurnoverOnDowns = false
) {
  if (currentPlay.isFirstDown === false && last(currentPlay.resultType) === TPlayResultTypes.FIRST_DOWN) {
    currentPlay.resultType.pop();
  }

  const lastType = last(currentPlay.resultType);

  // penalty enforced on a turnover that stood keeps the turnover as the play result
  const isPenaltyOnStandingTurnover =
    !!currentPlay.options?.isPenaltyOnTurnover &&
    !currentPlay.options?.isPossessionRetained &&
    [TPlayResultTypes.PENALTY_HOME, TPlayResultTypes.PENALTY_AWAY, TPlayResultTypes.PENALTY_BOTH].includes(
      lastType
    );

  if (
    (lastType === TPlayResultTypes.TOUCHBACK && currentPlay.options?.isTouchbackTov) ||
    isPenaltyOnStandingTurnover
  ) {
    const lastTurnoverType = currentPlay.resultType?.filter((type) => turnoverResults.includes(type)).pop();
    if (lastTurnoverType) {
      return lastTurnoverType;
    }
  }

  if (!lastType) {
    switch (currentPlay.penalty) {
      case TOffDef.OFFENSE:
        return offenseTeam === TTeam.HOME ? TPlayResultTypes.PENALTY_HOME : TPlayResultTypes.PENALTY_AWAY;
      case TOffDef.DEFENSE:
        return offenseTeam === TTeam.HOME ? TPlayResultTypes.PENALTY_AWAY : TPlayResultTypes.PENALTY_HOME;
      case TOffDef.BOTH:
        return TPlayResultTypes.PENALTY_BOTH;
      default:
        if (currentPlay.playDesignId === TPlayDesigns.PASS && !currentPlay.qbScramble) {
          return TPlayResultTypes.PASS_COMP;
        }
        return TPlayResultTypes.RUSH_ATTENPT;
    }
  }

  return getResultWithMultiTovCheck(lastType, currentPlay);
}

export const computePlayerStats = (
  players: { playerId: number; roleId: number }[],
  resultTypes: TPlayResultTypes[],
  distanceTraveled: number
): IPlayerStat[] =>
  (players || []).map((player) => ({
    ...player,
    statisticalYardage: distanceTraveled,
    ignoreAttempt: false,
    attempt: true,
    interception: player.roleId === TPlayerRole.PASSER && containsInterceptionResult(resultTypes),
    touchdown: containsOffensiveTouchdownResult(resultTypes),
    complete: player.roleId === TPlayerRole.PASSER && isCompletePass(resultTypes),
  }));

export function getPlayResults(
  currentPlay: IMachineContextCurrentPlay,
  activeTeam: TTeam,
  down: number,
  distance: number,
  driveDirection: TTeamSide,
  yardlineNumber: number,
  yardlineSide: TTeamSide
) {
  let resultingDistance: number | null;
  let resultingDown: number | null;
  let homeTeamEndedWithPossession: boolean;
  let isTurnoverOnDowns: boolean;
  let isDefiniteFirstDown: boolean;
  let distanceTraveled: number;

  if (
    !currentPlay.options?.isPatOrFgMake &&
    !currentPlay.options?.isScore &&
    !currentPlay.options?.isSaftey
  ) {
    ({
      down: resultingDown,
      distance: resultingDistance,
      isTurnoverOnDowns,
      isDefiniteFirstDown,
      distanceTraveled,
    } = getResultingDownAndDistance(
      down,
      distance,
      driveDirection,
      yardlineNumber,
      yardlineSide,
      currentPlay
    ));
    homeTeamEndedWithPossession = getIsHomeResultingPossession(currentPlay, activeTeam, isTurnoverOnDowns);
  } else if (
    currentPlay.playDesignId === TPlayDesigns.KICKOFF ||
    currentPlay.playDesignId === TPlayDesigns.ONSIDE ||
    currentPlay.playDesignId === TPlayDesigns.PUNT
  ) {
    // Kickoff/Punt returned or saftey should switch active
    // team from kicking team to the return team
    homeTeamEndedWithPossession = getIsHomeResultingPossession(currentPlay, activeTeam, isTurnoverOnDowns);
    resultingDown = null;
    resultingDistance = null;
  } else {
    // scores
    const wasTeamSwitched =
      !!currentPlay.options?.patReturned ||
      !!currentPlay.options?.isKickoffOrPunt ||
      !!currentPlay.options?.isTov ||
      !!currentPlay.options?.isTouchbackTov;
    homeTeamEndedWithPossession =
      (activeTeam === TTeam.HOME && !wasTeamSwitched) || (activeTeam === TTeam.AWAY && wasTeamSwitched);
    resultingDown = null;
    resultingDistance = null;
    ({ distanceTraveled } = getResultingDownAndDistance(
      down,
      distance,
      driveDirection,
      yardlineNumber,
      yardlineSide,
      currentPlay
    ));
  }

  return {
    isTurnoverOnDowns,
    resultingDown,
    resultingDistance,
    homeTeamEndedWithPossession,
    isDefiniteFirstDown,
    distanceTraveled,
  };
}

export function determinePlayDriveDirection(
  originalPlay: Partial<IPlayflowPlay>,
  teamSides: { left: TTeam; right: TTeam },
  isInsert = false
): TTeamSide {
  let teamWithBall = originalPlay.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY;
  if (isInsert) {
    teamWithBall = originalPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY;
  }

  if (teamSides.left === TTeam.HOME) {
    return teamWithBall === TTeam.HOME ? TTeamSide.RIGHT : TTeamSide.LEFT;
  }
  return teamWithBall === TTeam.HOME ? TTeamSide.LEFT : TTeamSide.RIGHT;
}

export const getFumbleOrPenaltyYardline = (
  teams: { left: TTeam; right: TTeam },
  startingYardLine: { number: number; side: TTeamSide },
  currentPlay: IMachineContextCurrentPlay
) => {
  const values: Pick<
    IPlayflowPlay,
    'fumbleYardLine' | 'fumbleIsHomeTeamSideOfField' | 'penaltyYardLine' | 'penaltyIsHomeTeamSideOfField'
  > = {
    fumbleYardLine: null,
    fumbleIsHomeTeamSideOfField: null,
    penaltyYardLine: null,
    penaltyIsHomeTeamSideOfField: null,
  };

  if (isNumber(currentPlay.fumbleYardLine) && currentPlay.fumbleSide) {
    values.fumbleYardLine = currentPlay.fumbleYardLine;
    values.fumbleIsHomeTeamSideOfField = getResultingSideOfField(
      teams,
      currentPlay.playDesignId,
      currentPlay.fumbleSide,
      false
    );
  } else if (last(currentPlay.resultType) === TPlayResultTypes.FUMBLE_LOST) {
    values.fumbleYardLine = startingYardLine.number;
    values.fumbleIsHomeTeamSideOfField = getResultingSideOfField(
      teams,
      currentPlay.playDesignId,
      startingYardLine.side,
      false
    );
  }

  if (isNumber(currentPlay.penaltyYardLine) && currentPlay.penaltySide) {
    values.penaltyYardLine = currentPlay.penaltyYardLine;
    values.penaltyIsHomeTeamSideOfField = getResultingSideOfField(
      teams,
      currentPlay.playDesignId,
      currentPlay.penaltySide,
      false
    );
  } else if (
    [TPlayResultTypes.PENALTY_AWAY, TPlayResultTypes.PENALTY_BOTH, TPlayResultTypes.PENALTY_HOME].includes(
      last(currentPlay.resultType)
    )
  ) {
    values.penaltyYardLine = startingYardLine.number;
    values.penaltyIsHomeTeamSideOfField = getResultingSideOfField(
      teams,
      currentPlay.playDesignId,
      startingYardLine.side,
      false
    );
  }

  return values;
};

export const nullifyDownAndDistanceIfNeccessary = (play: IPlayflowPlay): IPlayflowPlay => {
  if (touchdownResults.includes(play.playResultTypeId) || play.playResultTypeId === TPlayResultTypes.FG) {
    play.resultingDistance = null;
    play.resultingDown = null;
  } else if (
    play.playDesignId === TPlayDesigns.EXTRA_PT ||
    play.playDesignId === TPlayDesigns.TWO_PT_CONVERSION
  ) {
    play.startingDistance = null;
    play.startingDown = null;
    play.resultingDistance = null;
    play.resultingDown = null;
  } else if (play.playDesignId === TPlayDesigns.KICKOFF) {
    play.startingDistance = null;
    play.startingDown = null;
  }
  return play;
};

/**
 * Only use cached drive numbering if it is logically ordered after the latest play log play.
 * Cache could be behind if user A starts entering plays, user B takes over, then user A returns
 */
export const getDriveNumberingFromCache = (
  gameId: number,
  key: string,
  lastPlayInLog: IPlayflowPlay
): { driveNumber: number; drivePlayNumber: number } | null => {
  const cached = JSON.parse(localStorage.getItem(key) ?? '{}');
  if (!cached || !cached.gameId || cached.gameId !== gameId) return null;
  const ordered = orderBy([cached, lastPlayInLog], ['driveNumber', 'drivePlayNumber'], ['asc', 'asc']);
  if (last(ordered) !== cached) {
    return null;
  }
  return cached;
};

/// //////////////////////////////////////////////////////
/// Helper functions for determining next play state
/// TODO: these are very messy and should be refactored!!!
/// //////////////////////////////////////////////////////
export const isOvertimeScoringPlay = (
  currentQuarter: TQuarters,
  currentPlay: IMachineContextCurrentPlay
): boolean =>
  (currentQuarter === TQuarters.OT1 || currentQuarter === TQuarters.OT2) &&
  [TPlayDesigns.EXTRA_PT, TPlayDesigns.TWO_PT_CONVERSION, TPlayDesigns.FG].includes(
    currentPlay.playDesignId
  ) &&
  !currentPlay.options?.isPreSnapPATPenalty;

export const isOvertime3PlusScoringPlay = (
  currentQuarter: TQuarters,
  currentPlay: IMachineContextCurrentPlay
): boolean =>
  currentQuarter === TQuarters.OT3_PLUS && last(currentPlay.resultType) === TPlayResultTypes.CONVERSION_2PT;

export const isNextPlayKickoff = (currentPlay: IMachineContextCurrentPlay): boolean =>
  (currentPlay.options?.isPatOrFgMake && !currentPlay.options?.isPreSnapPATPenalty) ||
  currentPlay.options?.isSaftey;

export const isNoScorePossessionChange = (
  currentPlay: IMachineContextCurrentPlay,
  play: IPlayflowPlay,
  isTurnoverOnDowns: boolean
): boolean => {
  if (
    isPreSnapPenalty(currentPlay) ||
    currentPlay.options?.isKickReturnFumbleLost ||
    currentPlay.options?.isOnsideRecover ||
    (currentPlay.penalty === TOffDef.BOTH && currentPlay.playDesignId === TPlayDesigns.PUNT) ||
    !!currentPlay.options?.isPossessionRetained
  ) {
    return false;
  }

  if (
    (!currentPlay.options?.isScore &&
      ((currentPlay.options?.isKickoffOrPunt && !currentPlay.options?.isPrePuntSnapPotentialTov) ||
        currentPlay.options?.isTov) &&
      !currentPlay.penalty) ||
    (!currentPlay.options?.isScore && currentPlay.options?.isKickoff) ||
    isTurnoverOnDowns ||
    currentPlay.options?.isTouchbackTov ||
    currentPlay.options?.isPenaltyDuringReturn
  ) {
    return true;
  }

  if (
    play.homeTeamStartedWithPossession !== play.homeTeamEndedWithPossession &&
    [TPlayDesigns.PASS, TPlayDesigns.RUSH].includes(play.playDesignId) &&
    !currentPlay.options?.isScore
  ) {
    return true;
  }

  return false;
};

export const isNoScoreAndNoPossessionChange = (currentPlay: IMachineContextCurrentPlay): boolean =>
  (!currentPlay.options?.isScore &&
    (!(currentPlay.options?.isKickoffOrPunt || currentPlay.options?.isTov) ||
      (currentPlay.options?.isKickoffOrPunt &&
        currentPlay.options?.isPrePuntSnapPotentialTov &&
        !currentPlay.options?.isTov))) ||
  (!currentPlay.options?.isScore &&
    (currentPlay.options?.isKickoffOrPunt || currentPlay.options?.isTov) &&
    !!currentPlay.penalty) ||
  isPreSnapPenalty(currentPlay) ||
  (!currentPlay.options?.isScore && !!currentPlay.options?.isPossessionRetained) ||
  (!currentPlay.options?.isScore && currentPlay.options?.isKickReturnFumbleLost) ||
  !!currentPlay.options?.isOnsideRecover;
/// //////////////////////////////////////////////////
